import type { FormVariant } from "@homeinstead/analytics";
import type { Rating as OfficeRating } from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type {
  CareRecipient,
  CareSeekerShallowLeadFormData,
} from "~www/types/forms/care-seeker-shallow-lead-form";
import type { HeroIconVariant } from "~www/types/national-hero";
import type { HeroVariant } from "../shared/hero-variants";

import { cn } from "@homeinstead/ui";
import { Rating } from "@homeinstead/ui/components/Rating";
import { RichText } from "@homeinstead/ui/components/RichText";
import { Controller, useFormContext } from "react-hook-form";
import { TrackedPhoneLink } from "~www/components/TrackedPhoneLink";
import { MINIMUM_RATING_TO_DISPLAY } from "~www/lib/office/constants";
import { isDisplayableRating } from "~www/lib/office/get-aggregate-rating";
import { CareRecipientButton } from "../shared/CareRecipientButton";
import { RecipientOptions } from "../shared/form-options";
import { getHeroVariantStyles } from "../shared/hero-variants";

interface CareRecipientStepProps {
  nextStep: () => void;
  className?: string;
  heroHeading: PortableTextBlock[];
  placeholders?: Record<string, string>;
  phoneNumber: string;
  officeRating?: OfficeRating;
  iconVariant?: HeroIconVariant | "outlined";
  variant?: HeroVariant;
  formVariant: FormVariant;
}

const iconColorMap: Record<string, Record<number, string> | string> = {
  holiday: {
    0: "fill-[#EF5574] group-hover:fill-new-beginnings-base",
    1: "fill-[#82AFD7] group-hover:fill-new-beginnings-base",
    2: "fill-[#859A5E] group-hover:fill-new-beginnings-base",
    3: "fill-new-beginnings-base group-hover:fill-new-beginnings-base",
  },
  outlined: "fill-new-beginnings-base",
  primary: "fill-friendly-base group-hover:fill-new-beginnings-base",
};

const CareRecipientStepV2 = ({
  nextStep,
  className,
  heroHeading,
  placeholders,
  phoneNumber,
  officeRating,
  iconVariant = "primary",
  variant = "default",
  formVariant,
}: CareRecipientStepProps) => {
  const { control } = useFormContext<CareSeekerShallowLeadFormData>();

  const { textColorClass, linkVariant } = getHeroVariantStyles(variant);

  const canDisplayRating = isDisplayableRating(officeRating);

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-20 xl:flex-row xl:gap-y-4 xl:pt-16",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-4 xl:items-start">
        <div
          className={cn(
            "text-left md:text-center xl:w-[455px] xl:text-left [&_.text-md]:font-bold",
            textColorClass,
          )}
        >
          <RichText placeholders={placeholders} value={heroHeading} />
        </div>

        {/* Star Rating - directly below H1 */}
        {canDisplayRating ? (
          <Rating
            className="mb-6 self-start md:self-center xl:self-start"
            minimumRating={MINIMUM_RATING_TO_DISPLAY}
            rating={officeRating.rating}
            ratingCount={officeRating.ratingCount}
            service={officeRating.ratingService}
            textClassname="font-bold"
            variant={variant === "light" ? "light" : "default"}
          />
        ) : null}

        {/* CTA Headline */}
        <p
          className={cn(
            "w-full text-left font-bold text-lg md:text-center xl:text-left",
            textColorClass,
          )}
        >
          Who is the care for?
        </p>

        <Controller
          control={control}
          name="careFor"
          render={({ field }) => (
            <div className="flex w-full flex-col gap-4 md:w-[250px] md:gap-3">
              <input type="hidden" {...field} />
              {RecipientOptions.map((option, idx) => {
                const Icon = option.icon;

                const optionFill =
                  iconVariant === "holiday"
                    ? iconColorMap.holiday[idx]
                    : iconColorMap[iconVariant];
                return (
                  <CareRecipientButton
                    buttonVariant="outline"
                    careRecipient={option.value as CareRecipient}
                    className="group flex h-12 w-full justify-start gap-4 rounded-full px-6 font-semibold md:w-auto"
                    field={field}
                    formVariant={formVariant}
                    key={option.value}
                    nextStepCallback={nextStep}
                  >
                    <Icon className={cn("size-6", optionFill)} />
                    <span>{option.label}</span>
                  </CareRecipientButton>
                );
              })}

              {/* Helper text */}
              <p
                className={cn(
                  "mx-auto mt-2 w-[300px] text-center font-semibold text-md md:text-left",
                  textColorClass,
                )}
              >
                Find the right care and cost with a no&#8209;pressure, free
                consultation.
              </p>

              {/* Call Us CTA */}
              <TrackedPhoneLink
                className="mt-4 flex h-12 justify-center bg-new-beginnings-base px-6 text-white hover:text-new-beginnings-base md:w-auto"
                phoneNumber={phoneNumber}
                placement="lead form"
                variant={linkVariant}
              >
                Call Us: {phoneNumber}
              </TrackedPhoneLink>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default CareRecipientStepV2;
